import type { CSSProperties } from 'react';
import type { Photo as PhotoData } from '../types';

type Props = {
  photo: PhotoData;
  /** Aspect ratio when not in fill mode. */
  ratio?: string;
  /** Native sizes attr (e.g. "(min-width: 768px) 50vw, 100vw"). */
  sizes?: string;
  /** Fill the parent container instead of using an aspect ratio. */
  fill?: boolean;
  /** "cover" crops to fit; "contain" letterboxes (use in lightbox). */
  fit?: 'cover' | 'contain';
  className?: string;
  loading?: 'lazy' | 'eager';
  /** Maps to the img `fetchPriority` attribute. */
  priority?: 'high' | 'low' | 'auto';
  /** Overrides photo.caption — pass when context describes the image better than the filename slug. */
  alt?: string;
};

export default function Photo({
  photo,
  ratio = '4/3',
  sizes = '100vw',
  fill,
  fit = 'cover',
  className,
  loading = 'lazy',
  priority,
  alt,
}: Props) {
  const wrapperStyle: CSSProperties = fill
    ? { width: '100%', height: '100%' }
    : { aspectRatio: ratio };

  const imgClass = [
    'block w-full select-none',
    fill ? 'h-full' : '',
    fit === 'cover' ? 'object-cover' : 'object-contain h-full',
    '[-webkit-user-drag:none] [-webkit-touch-callout:none]',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <picture
      className={`relative block ${className ?? ''}`}
      style={wrapperStyle}
      onContextMenu={(e) => e.preventDefault()}
    >
      {photo.webpSrcSet && (
        <source type="image/webp" srcSet={photo.webpSrcSet} sizes={sizes} />
      )}
      <img
        src={photo.fallback}
        srcSet={photo.jpgSrcSet || undefined}
        sizes={sizes}
        alt={alt ?? photo.caption}
        loading={loading}
        decoding="async"
        draggable={false}
        className={imgClass}
        style={fill ? undefined : { aspectRatio: ratio }}
        fetchPriority={priority}
      />
    </picture>
  );
}
